import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MessageSquarePlus, Lightbulb, Bug, MessageSquare, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SubmitFeatureDialog } from './SubmitFeatureDialog';
import { FeedbackDialog } from './FeedbackDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingComplete } from '@/hooks/useOnboardingComplete';
import { useStickyActionBarHeight, STICKY_ACTION_BAR_CLEARANCE } from '@/hooks/useStickyActionBar';
import { FAB_LAYER_CLASS, isFabHiddenRoute, onFeedbackRequest } from '@/lib/feedbackLauncher';

/** Resting distance from the bottom edge, in px (was `bottom-6` / `bottom-24`).
 *  The larger value keeps clear of the onboarding checklist. */
const RESTING_BOTTOM_PX = 24;
const RESTING_BOTTOM_WITH_ONBOARDING_PX = 96;

/** Distance from the button to the menu above it (was `bottom-[7.5rem]` / `bottom-[9.5rem]`). */
const MENU_OFFSET_PX = 96;
const MENU_OFFSET_WITH_ONBOARDING_PX = 56;

export function FloatingFeedbackButton() {
  const { user } = useAuth();
  const onboardingComplete = useOnboardingComplete();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'bug' | 'feedback' | 'praise'>('feedback');

  const { t } = useTranslation('roadmap');
  const stickyBarHeight = useStickyActionBarHeight();

  // Das Kontomenue (und kuenftig jeder andere Einstieg) oeffnet die Dialoge
  // ueber `requestFeedback`, auch dort, wo der Knopf selbst ausgeblendet ist.
  useEffect(
    () =>
      onFeedbackRequest((kind) => {
        setMenuOpen(false);
        if (kind === 'feature') {
          setFeatureDialogOpen(true);
          return;
        }
        setFeedbackType(kind);
        setFeedbackDialogOpen(true);
      }),
    [],
  );

  // A fixed action bar owns the bottom of the screen; the button moves above
  // it. `Math.max`: the onboarding checklist is a clearance too.
  const restingBottom = onboardingComplete ? RESTING_BOTTOM_PX : RESTING_BOTTOM_WITH_ONBOARDING_PX;
  const barBottom = stickyBarHeight > 0 ? stickyBarHeight + STICKY_ACTION_BAR_CLEARANCE : 0;
  const buttonBottom = Math.max(restingBottom, barBottom);
  const menuBottom =
    buttonBottom + (onboardingComplete ? MENU_OFFSET_PX : MENU_OFFSET_WITH_ONBOARDING_PX);

  if (!user) return null;

  // K07: auf der Werkbank lag der Knopf auf "Naechstes Produkt". Die Dialoge
  // bleiben montiert, damit `requestFeedback` dort weiter funktioniert.
  const hideButton = isFabHiddenRoute(location.pathname);

  const openFeedback = (type: 'bug' | 'feedback' | 'praise') => {
    setFeedbackType(type);
    setFeedbackDialogOpen(true);
    setMenuOpen(false);
  };

  return (
    <>
      {!hideButton && menuOpen && (
        <>
          <div
            className={`fixed inset-0 ${FAB_LAYER_CLASS} print:hidden`}
            onClick={() => setMenuOpen(false)}
          />
          <div
            className={`fixed right-6 ${FAB_LAYER_CLASS} flex flex-col gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200 print:hidden`}
            style={{ bottom: `${menuBottom}px` }}
            data-testid="floating-feedback-menu"
          >
            <button
              onClick={() => { setFeatureDialogOpen(true); setMenuOpen(false); }}
              className="flex min-h-11 items-center gap-3 px-4 py-2.5 bg-background border border-border rounded-lg shadow-lg hover:bg-accent transition-colors text-sm whitespace-nowrap"
            >
              <Lightbulb className="h-4 w-4 text-warning" />
              {t('floatingButton.suggestFeature')}
            </button>
            <button
              onClick={() => openFeedback('bug')}
              className="flex min-h-11 items-center gap-3 px-4 py-2.5 bg-background border border-border rounded-lg shadow-lg hover:bg-accent transition-colors text-sm whitespace-nowrap"
            >
              <Bug className="h-4 w-4 text-destructive" />
              {t('floatingButton.reportBug')}
            </button>
            <button
              onClick={() => openFeedback('feedback')}
              className="flex min-h-11 items-center gap-3 px-4 py-2.5 bg-background border border-border rounded-lg shadow-lg hover:bg-accent transition-colors text-sm whitespace-nowrap"
            >
              <MessageSquare className="h-4 w-4 text-info" />
              {t('floatingButton.giveFeedback')}
            </button>
          </div>
        </>
      )}

      {!hideButton && (
        <Button
          size="icon"
          className={`fixed right-6 ${FAB_LAYER_CLASS} h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all print:hidden`}
          style={{ bottom: `${buttonBottom}px` }}
          data-testid="floating-feedback-button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={menuOpen ? t('floatingButton.closeMenu') : t('floatingButton.openMenu')}
        >
          {menuOpen ? <X className="h-5 w-5" /> : <MessageSquarePlus className="h-5 w-5" />}
        </Button>
      )}

      <SubmitFeatureDialog open={featureDialogOpen} onClose={() => setFeatureDialogOpen(false)} />
      <FeedbackDialog open={feedbackDialogOpen} onClose={() => setFeedbackDialogOpen(false)} defaultType={feedbackType} />
    </>
  );
}
